import { t as uiText } from '../i18n';
import React, { useState, useRef, useEffect } from 'react';
import { optimizeImageForUpload } from '../features/workspace/imageUtils';

interface ImageCropModalProps {
  imageFile: File;
  cropType?: 'square' | 'circle';
  onCropComplete: (croppedFile: File, dataUrl: string) => void;
  onCancel: () => void;
  overlayZIndex?: number;
}

const ImageCropModal: React.FC<ImageCropModalProps> = ({
  imageFile,
  cropType = 'square',
  onCropComplete,
  onCancel,
  overlayZIndex = 10001,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [cropSize, setCropSize] = useState(200);
  const [imageData, setImageData] = useState<{
    width: number;
    height: number;
    scale: number;
    offsetX: number;
    offsetY: number;
  } | null>(null);
  const [imageSrc, setImageSrc] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isProcessing, setIsProcessing] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);

  // 画像を読み込み
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // キャンバスサイズを設定
      canvas.width = cropSize;
      canvas.height = cropSize;

      // 画像のスケールとオフセットを計算
      const baseScale = Math.max(cropSize / img.width, cropSize / img.height);
      const scale = baseScale * zoomLevel;
      const scaledWidth = img.width * scale;
      const scaledHeight = img.height * scale;
      const offsetX = (cropSize - scaledWidth) / 2;
      const offsetY = (cropSize - scaledHeight) / 2;

      setImageData({
        width: img.width,
        height: img.height,
        scale,
        offsetX,
        offsetY
      });

      // 背景を白で塗りつぶし
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, cropSize, cropSize);
      
      // 丸クロップの場合は円形マスクを適用
      if (cropType === 'circle') {
        ctx.save();
        ctx.beginPath();
        ctx.arc(cropSize / 2, cropSize / 2, cropSize / 2, 0, Math.PI * 2);
        ctx.clip();
      }
      
      // 画像を描画
      ctx.drawImage(img, offsetX, offsetY, scaledWidth, scaledHeight);
      
      // 丸クロップの場合はマスクを解除
      if (cropType === 'circle') {
        ctx.restore();
      }
      setImageLoaded(true);
    };

    img.onerror = () => {
      console.error('Failed to load the image');
      alert(uiText("Failed to load the image"));
    };

    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };
    reader.onerror = () => {
      console.error('Failed to load file');
      alert(uiText("Failed to load file"));
    };
    reader.readAsDataURL(imageFile);
  }, [imageFile, cropSize, zoomLevel]);

  // 画像を再描画する関数
  const redrawImage = (newOffsetX: number, newOffsetY: number) => {
    if (!imageData || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // キャンバスをクリア
    ctx.clearRect(0, 0, cropSize, cropSize);
    
    // 背景を白で塗りつぶし
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, cropSize, cropSize);
    
    // 画像を描画
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(
        img,
        newOffsetX,
        newOffsetY,
        imageData.width * imageData.scale,
        imageData.height * imageData.scale
      );
    };
    img.src = imageSrc;

    // 状態を更新
    setImageData(prev => prev ? {
      ...prev,
      offsetX: newOffsetX,
      offsetY: newOffsetY
    } : null);
  };



  // ズーム機能
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(Math.round((prev + 0.1) * 10) / 10, 3)); // 最大3倍
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(Math.round((prev - 0.1) * 10) / 10, 0.5)); // 最小0.5倍
  };

  // クロップ実行
  const handleCrop = async () => {
    if (!canvasRef.current || !imageLoaded) return;

    setIsProcessing(true);

    try {
      // 丸クロップの場合は新しいキャンバスで円形マスクを適用
      if (cropType === 'circle') {
        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d');
        if (!tempCtx) {
          alert(uiText("Failed to process the image"));
          setIsProcessing(false);
          return;
        }

        tempCanvas.width = cropSize;
        tempCanvas.height = cropSize;

        // 円形マスクを適用
        tempCtx.save();
        tempCtx.beginPath();
        tempCtx.arc(cropSize / 2, cropSize / 2, cropSize / 2, 0, Math.PI * 2);
        tempCtx.clip();
        tempCtx.drawImage(canvasRef.current, 0, 0);
        tempCtx.restore();

        // 円形マスク適用後のキャンバスからBlobを取得
        tempCanvas.toBlob(async (blob) => {
          if (!blob) {
            alert(uiText("Failed to process the image"));
            setIsProcessing(false);
            return;
          }

          // Fileオブジェクトを作成
          const croppedFile = new File([blob], imageFile.name, {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });

          // 最適化
          const optimized = await optimizeImageForUpload(croppedFile, { quality: 0.5 });

          // 最適化されたファイルをDataURLに変換
          const reader = new FileReader();
          reader.onload = (e) => {
            const dataUrl = e.target?.result as string;
            onCropComplete(optimized, dataUrl);
            setIsProcessing(false);
          };
          reader.readAsDataURL(optimized);
        }, 'image/jpeg', 0.9);
      } else {
        // 四角クロップの場合は既存の処理
        canvasRef.current.toBlob(async (blob) => {
          if (!blob) {
            alert(uiText("Failed to process the image"));
            setIsProcessing(false);
            return;
          }

          // Fileオブジェクトを作成
          const croppedFile = new File([blob], imageFile.name, {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });

          // 最適化
          const optimized = await optimizeImageForUpload(croppedFile, { quality: 0.5 });

          // 最適化されたファイルをDataURLに変換
          const reader = new FileReader();
          reader.onload = (e) => {
            const dataUrl = e.target?.result as string;
            onCropComplete(optimized, dataUrl);
            setIsProcessing(false);
          };
          reader.readAsDataURL(optimized);
        }, 'image/jpeg', 0.9);
      }
    } catch (error) {
      console.error('クロップ処理エラー:', error);
      alert(uiText("Failed to process the image"));
      setIsProcessing(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: overlayZIndex,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onCancel();
        }
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          padding: '24px',
          maxWidth: '90vw',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{
          margin: '0 0 20px 0',
          fontSize: '18px',
          fontWeight: 'bold',
          color: '#333',
          textAlign: 'center'
        }}>
          {uiText("Crop image")}
        </h3>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          {/* キャンバス */}
          <div style={{
            border: '2px solid var(--workspace-primary, #cfae91)',
            borderRadius: cropType === 'circle' ? '50%' : '8px',
            overflow: 'hidden'
          }}>
            <canvas
              ref={canvasRef}
              width={cropSize}
              height={cropSize}
              style={{
                display: 'block',
                maxWidth: '100%',
                height: 'auto'
              }}
            />
          </div>



          {/* ズームコントロール */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            marginTop: '12px'
          }}>
            <button
              onClick={handleZoomOut}
              disabled={zoomLevel <= 0.5}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                border: '2px solid var(--workspace-primary, #cfae91)',
                backgroundColor: zoomLevel <= 0.5 ? '#f5f5f5' : '#fff',
                color: zoomLevel <= 0.5 ? '#ccc' : 'var(--workspace-primary, #cfae91)',
                fontSize: '18px',
                fontWeight: 'bold',
                cursor: zoomLevel <= 0.5 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (zoomLevel > 0.5) {
                  e.currentTarget.style.backgroundColor = 'var(--workspace-primary, #cfae91)';
                  e.currentTarget.style.color = '#fff';
                }
              }}
              onMouseLeave={(e) => {
                if (zoomLevel > 0.5) {
                  e.currentTarget.style.backgroundColor = '#fff';
                  e.currentTarget.style.color = 'var(--workspace-primary, #cfae91)';
                }
              }}
            >
              −
            </button>
            
            <div style={{
              fontSize: '14px',
              color: '#666',
              minWidth: '60px',
              textAlign: 'center'
            }}>
              {Math.round(zoomLevel * 100)}%
            </div>
            
            <button
              onClick={handleZoomIn}
              disabled={zoomLevel >= 3}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                border: '2px solid var(--workspace-primary, #cfae91)',
                backgroundColor: zoomLevel >= 3 ? '#f5f5f5' : '#fff',
                color: zoomLevel >= 3 ? '#ccc' : 'var(--workspace-primary, #cfae91)',
                fontSize: '18px',
                fontWeight: 'bold',
                cursor: zoomLevel >= 3 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (zoomLevel < 3) {
                  e.currentTarget.style.backgroundColor = 'var(--workspace-primary, #cfae91)';
                  e.currentTarget.style.color = '#fff';
                }
              }}
              onMouseLeave={(e) => {
                if (zoomLevel < 3) {
                  e.currentTarget.style.backgroundColor = '#fff';
                  e.currentTarget.style.color = 'var(--workspace-primary, #cfae91)';
                }
              }}
            >
              +
            </button>
          </div>

          {/* 説明 */}
          <div style={{
            fontSize: '14px',
            color: '#666',
            textAlign: 'center',
            maxWidth: '300px'
          }}>
            {uiText("Use the zoom buttons to resize the image.")}<br />
            {uiText("Used as a square icon.")}
          </div>

          {/* ボタン */}
          <div style={{
            display: 'flex',
            gap: '12px',
            marginTop: '16px'
          }}>
            <button
              onClick={onCancel}
              disabled={isProcessing}
              style={{
                padding: '8px 16px',
                backgroundColor: '#f5f5f5',
                color: '#333',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                opacity: isProcessing ? 0.6 : 1
              }}
            >
              {uiText("Cancel")}
            </button>
            <button
              onClick={handleCrop}
              disabled={!imageLoaded || isProcessing}
              style={{
                padding: '8px 16px',
                backgroundColor: isProcessing ? '#ccc' : 'var(--workspace-primary, #cfae91)',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: (!imageLoaded || isProcessing) ? 'not-allowed' : 'pointer',
                opacity: (!imageLoaded || isProcessing) ? 0.6 : 1
              }}
            >
              {isProcessing ? uiText("Processing...") : uiText("Crop done")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCropModal; 